import React from 'react';
import { Body7, Container } from '@ui';
import { ChipRow, FilterChip } from './FilterChip';
import { Disclosure } from './Disclosure';
import { allDisplayColumns, canHideColumn, unloadedVisibleColumns } from './columns';
import { listOf } from './format';

// Show or hide any column in the table. Open by default — the toggles are worth seeing without
// hunting for them — but collapsible, because a wide feed's worth of them sits between the query and
// the table. The heading carries the hidden count so the state stays visible while the list isn't.
//
// What is on offer is this feed's own column universe, so the same table can offer sector for one
// feed and not for its sibling. Two different questions live behind the toggles: sector, market cap
// and the index flags reach the table only because the request named them, so turning one off stops
// the next request naming it. The company, the score, the timestamp and the member columns arrive
// regardless, so hiding those is a display choice this table makes on its own.
//
// A third state exists between on and off, and it is the one worth being explicit about: the feed
// declares a column, nothing turned it off, and it still isn't in the table — because the read that
// produced these rows never named it. Such a toggle is on and waiting for a re-run, not broken, and
// the table is right to draw nothing rather than a column of "None".
export const ColumnChooser = ({ result, hiddenColumns, onToggle }) => {
  const [isOpen, setIsOpen] = React.useState(true);

  const columns = allDisplayColumns(result);
  if (columns.length === 0) return null;

  const hiddenCount = columns.filter((column) => hiddenColumns.includes(column.key)).length;
  const hidingAnyOmittable = columns.some((column) => column.omittable && hiddenColumns.includes(column.key));
  // Shared with the page, which puts a button on this exact set — see unloadedVisibleColumns for why
  // a hidden column is not one of them.
  const awaitingRead = unloadedVisibleColumns(result, hiddenColumns);

  return (
    <Container bottom={16}>
      <Disclosure
        isOpen={isOpen}
        onToggle={() => setIsOpen((prev) => !prev)}
        summary={
          <>
            <Body7 bold>Columns</Body7>
            {hiddenCount > 0 && <Body7 isLowContrast>{`${hiddenCount} hidden`}</Body7>}
          </>
        }
      >
        <Container top={8}>
          <ChipRow>
            {columns.map((column) => {
              const isVisible = !hiddenColumns.includes(column.key);
              const isLast = !canHideColumn(result, hiddenColumns, column.key);
              // aria-pressed is the state of the SWITCH, which is what the button controls; whether
              // the column made it into these rows is a separate fact, carried by the title and the
              // note below so pressing it stays predictable.
              const isPending = isVisible && !column.loaded;
              return (
                <FilterChip
                  key={column.key}
                  pressed={isVisible}
                  pending={isPending}
                  label={`${column.label} column`}
                  title={
                    isLast
                      ? 'The last visible column can’t be hidden'
                      : isPending
                      ? 'On, but not in these rows — re-run to load it'
                      : undefined
                  }
                  onPress={() => onToggle(column.key)}
                >
                  {column.label}
                </FilterChip>
              );
            })}
          </ChipRow>
          {awaitingRead.length > 0 && (
            <Container top={4}>
              <Body7 isLowContrast>
                {`${listOf(awaitingRead.map((column) => column.label))} ${
                  awaitingRead.length === 1 ? 'is' : 'are'
                } switched on but not in these rows — the read that fetched them didn’t ask.`}
              </Body7>
            </Container>
          )}
          {hidingAnyOmittable && (
            <Container top={4}>
              <Body7 isLowContrast>
                Sector, market cap and indices are asked for by name, so the next request simply won’t ask — nothing is
                dropped, and the response is smaller. Hiding any other column affects this table only.
              </Body7>
            </Container>
          )}
        </Container>
      </Disclosure>
    </Container>
  );
};
